import { Download, Clock } from "lucide-react";
import type { DocumentVersion } from "../types";

function formatSize(bytes: number | null) {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("pl-PL", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

interface Props {
  versions: DocumentVersion[];
}

export function VersionHistoryList({ versions }: Props) {
  if (!versions.length) {
    return <p className="text-sm text-gray-400 italic">Brak wersji.</p>;
  }

  return (
    <ol className="flex flex-col gap-2">
      {versions.map((v, i) => (
        <li
          key={v.id}
          className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40"
        >
          {/* Version badge */}
          <span className="flex-shrink-0 text-xs font-bold bg-[var(--color-primary)] text-white rounded-full w-7 h-7 flex items-center justify-center mt-0.5">
            v{v.version_number}
          </span>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate max-w-xs">
                {v.original_name}
              </span>
              {i === 0 && (
                <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-1.5 py-0.5 rounded-full">
                  Aktualna
                </span>
              )}
              {formatSize(v.file_size) && (
                <span className="text-xs text-gray-400">{formatSize(v.file_size)}</span>
              )}
            </div>

            {v.change_comment && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{v.change_comment}</p>
            )}

            <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
              <Clock size={11} />
              <span>{formatDate(v.created_at)}</span>
              {v.uploaded_by_name && <span>· {v.uploaded_by_name}</span>}
            </div>
          </div>

          <a
            href={v.url}
            download={v.original_name}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-1 text-xs text-[var(--color-primary)] dark:text-blue-400 hover:underline mt-1"
          >
            <Download size={13} />
            Pobierz
          </a>
        </li>
      ))}
    </ol>
  );
}
